import type { Rate, RateInput } from "@voicely/zod-schemas/costing.zod";
import { z } from "zod";
import { istToday } from "./cost";

/**
 * Which rate the editor is open on, as URL state.
 *
 * `?edit=new`, `?edit=replace:<rateId>`, `?edit=period:<rateId>` and the two
 * worklist-driven `unpriced` / `postcall` intents. The dialog's open state
 * and what it is *for* both survive F5 and travel in a link; the prefill for
 * a replace or a new period is rebuilt from the rate book, so it survives
 * too. What a worklist row prefilled cannot be — a binding is not a row with
 * an id — so on a reload those two reopen with their title and empty fields,
 * which is honest about what the URL can carry.
 */
export const editParamZod = z
	.string()
	.regex(/^(new|unpriced|postcall|replace:[\w-]+|period:[\w-]+)$/)
	.nullable();

export type EditIntent = z.infer<typeof editParamZod>;

export interface RateEditing {
	title: string;
	initial?: Partial<RateInput>;
}

const TITLES: Record<"new" | "unpriced" | "postcall", string> = {
	new: "New rate",
	unpriced: "New rate for unpriced traffic",
	postcall: "New post-call rate",
};

/**
 * The editor's title and prefill for an intent, or null when it is closed —
 * including for an intent naming a rate that is no longer in the book, which
 * is a stale link and degrades to "closed" rather than to a dialog over
 * nothing.
 */
export function resolveRateEditing(
	intent: EditIntent,
	rates: readonly Rate[],
	prefill?: Partial<RateInput>,
): RateEditing | null {
	if (intent === null) return null;

	if (intent === "new" || intent === "unpriced" || intent === "postcall") {
		return prefill ? { title: TITLES[intent], initial: prefill } : { title: TITLES[intent] };
	}

	const [kind, id] = intent.split(":", 2);
	const rate = rates.find((entry) => entry.id === id);
	if (!rate) return null;

	if (kind === "replace") {
		return {
			title: "Replace rate",
			initial: {
				...rate,
				source: rate.source ?? undefined,
				note: rate.note ?? undefined,
				supersedes: rate.id,
			},
		};
	}

	return {
		title: "New period for this scope",
		initial: { ...rate, source: undefined, note: undefined, validFrom: istToday(), validTo: null },
	};
}
